import MessagesView from '../views/MessagesView';
import TeamView from '../views/TeamView';
import { useEffect, useRef, useState } from 'react';
import {
  Bell,
  BellRing,
  Check,
  Fingerprint,
  LogOut,
  MessageCircle,
  User,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useUiStore, Section } from '../store/ui';
import { initials } from '../lib/dates';
import {
  disablePush,
  enablePush,
  getPushState,
  PushState,
} from '../api/push';
import {
  getWebauthnStatus,
  registerPasskey,
  webauthnSupported,
} from '../api/webauthn';
import TodayView from '../views/TodayView';
import PlanningView from '../views/PlanningView';
import StudentsView from '../views/StudentsView';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import BilanModal from './BilanModal';
import LivretModal from './LivretModal';
import EvaluationModal from './EvaluationModal';
import LessonsModal from './LessonsModal';
import InstallAppBanner from './InstallAppBanner';

const TABS: Array<{ key: Section; label: string }> = [
  { key: 'jour', label: 'Ma journée' },
  { key: 'planning', label: 'Mon planning' },
  { key: 'eleves', label: 'Mes élèves' },
  { key: 'equipe', label: 'Équipe' },
  { key: 'messages', label: 'Messages' },
];

/**
 * Pastille du total de messages non lus (équipe et élèves réunis), dans la
 * navigation. `className` la positionne (`relative` ou `absolute`) : son texte pour
 * lecteurs d'écran est hors flux et élargirait sinon la page sur téléphone.
 */
function UnreadBadge({
  count,
  className = '',
}: {
  count: number;
  className?: string;
}) {
  if (count <= 0) return null;
  return (
    <span
      className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold leading-none ${className}`}
    >
      <span aria-hidden="true">{count > 99 ? '99+' : count}</span>
      <span className="sr-only">
        {` : ${count} non lu${count > 1 ? 's' : ''}`}
      </span>
    </span>
  );
}

export default function Layout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const section = useUiStore((s) => s.section);
  const setSection = useUiStore((s) => s.setSection);
  const unread = useUnreadMessages(section === 'messages');
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pushState, setPushState] = useState<PushState>('default');
  const [pushBusy, setPushBusy] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const [bioRegistered, setBioRegistered] = useState<boolean | null>(null);
  const [bioBusy, setBioBusy] = useState(false);

  // État de la connexion biométrique quand on ouvre le menu du compte.
  useEffect(() => {
    if (!menuOpen || !webauthnSupported() || bioRegistered !== null) return;
    getWebauthnStatus()
      .then(setBioRegistered)
      .catch(() => setBioRegistered(false));
  }, [menuOpen, bioRegistered]);

  const enrollBiometric = async () => {
    setBioBusy(true);
    try {
      await registerPasskey();
      setBioRegistered(true);
    } catch {
      // silencieux : l'utilisateur a annulé ou l'appareil ne supporte pas.
    } finally {
      setBioBusy(false);
    }
  };

  // Notifications déjà autorisées au niveau navigateur ?
  const notifAlreadyGranted =
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted';

  // Ré-abonnement silencieux au montage si la permission est déjà accordée,
  // pour que le serveur ait toujours un abonnement valide pour cet appareil.
  useEffect(() => {
    let cancelled = false;
    getPushState()
      .then(async (st) => {
        if (cancelled) return;
        if (
          st !== 'subscribed' &&
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted'
        ) {
          try {
            const next = await enablePush();
            if (!cancelled) setPushState(next);
            return;
          } catch {
            // best-effort
          }
        }
        setPushState(st);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      setPushState(
        pushState === 'subscribed' ? await disablePush() : await enablePush(),
      );
    } catch {
      // ignore
    } finally {
      setPushBusy(false);
    }
  };

  // Fermeture des menus au clic extérieur.
  useEffect(() => {
    if (!menuOpen && !notifOpen) return;
    const onClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen, notifOpen]);

  const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Moniteur';
  const avatar = initials(user?.firstName ?? 'M', user?.lastName ?? 'O');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="h-16 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <img src="/logo-permio.png" alt="Permio" className="h-7 w-auto flex-shrink-0" />
              {user?.schoolName && (
                <>
                  <div className="hidden sm:block h-6 w-px bg-slate-200" />
                  <span className="hidden sm:block text-xs text-slate-500 truncate">
                    {user.schoolName}
                  </span>
                </>
              )}
            </div>

            {/* Les onglets ne se replient pas : à l'étroit, c'est le nom de l'école qui s'abrège. */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8 h-16 flex-shrink-0">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setSection(t.key)}
                  className={`h-16 border-b-2 text-sm font-medium whitespace-nowrap transition-colors px-1 ${
                    section === t.key
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {t.label}
                  {t.key === 'messages' && (
                    <UnreadBadge
                      count={unread}
                      className="relative ml-1.5 align-middle"
                    />
                  )}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Sur téléphone les onglets défilent et « Messages » sort de l'écran : ses non-lus restent visibles ici. */}
              <button
                onClick={() => setSection('messages')}
                className="lg:hidden relative p-2.5 rounded-lg hover:bg-slate-100 transition-colors"
                aria-label={
                  unread > 0
                    ? `Ouvrir les messages, ${unread} non lu${unread > 1 ? 's' : ''}`
                    : 'Ouvrir les messages'
                }
              >
                <MessageCircle
                  size={20}
                  className={
                    section === 'messages' ? 'text-indigo-600' : 'text-slate-600'
                  }
                />
                <UnreadBadge
                  count={unread}
                  className="absolute -top-0.5 -right-0.5"
                />
              </button>
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setNotifOpen((v) => !v)}
                  className="relative p-2.5 rounded-lg hover:bg-slate-100 transition-colors"
                  aria-label="Notifications"
                >
                  {pushState === 'subscribed' ? (
                    <BellRing size={20} className="text-indigo-600" />
                  ) : (
                    <Bell size={20} className="text-slate-600" />
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                      <div className="text-sm font-semibold text-slate-900">
                        Notifications
                      </div>
                    </div>
                    <div className="p-4">
                      {pushState === 'unsupported' ? (
                        <p className="text-sm text-slate-500">
                          Notifications non disponibles sur ce navigateur.
                        </p>
                      ) : pushState === 'subscribed' ? (
                        <div className="space-y-3">
                          <p className="flex items-center gap-2 text-sm text-slate-700">
                            <Check size={16} className="text-emerald-600" />
                            Notifications activées sur cet appareil.
                          </p>
                          <p className="text-xs text-slate-500">
                            Vous serez prévenu·e des nouvelles leçons planifiées
                            et des réservations en ligne de vos élèves.
                          </p>
                          <button
                            onClick={togglePush}
                            disabled={pushBusy}
                            className="text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50"
                          >
                            {pushBusy ? '…' : 'Désactiver les notifications'}
                          </button>
                        </div>
                      ) : pushState === 'denied' ? (
                        <p className="text-sm text-slate-600">
                          Notifications bloquées. Autorisez-les pour Permio dans
                          les réglages de votre navigateur.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-slate-700">
                            Activez les notifications pour être prévenu·e des
                            nouvelles leçons et réservations en ligne.
                          </p>
                          {!notifAlreadyGranted && (
                            <button
                              onClick={togglePush}
                              disabled={pushBusy}
                              className="w-full py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50"
                            >
                              {pushBusy ? '…' : 'Activer les notifications'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative">
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 text-white flex items-center justify-center text-xs font-semibold hover:ring-2 hover:ring-violet-200 transition-all"
                >
                  {avatar}
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-fade-in">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                      <div className="text-sm font-semibold text-slate-900">{name}</div>
                      <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    </div>
                    <div className="p-1.5">
                      <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors text-left">
                        <User size={15} className="text-slate-500" />
                        Mon profil
                      </button>
                      {webauthnSupported() &&
                        (bioRegistered ? (
                          <div className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-500 text-left">
                            <Fingerprint size={15} className="text-emerald-600" />
                            FaceID activé
                          </div>
                        ) : (
                          <button
                            onClick={enrollBiometric}
                            disabled={bioBusy}
                            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors text-left disabled:opacity-60"
                          >
                            <Fingerprint size={15} className="text-indigo-600" />
                            {bioBusy ? 'Activation…' : 'Activer FaceID'}
                          </button>
                        ))}
                      <button
                        onClick={() => logout()}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors text-left"
                      >
                        <LogOut size={15} />
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Nav mobile */}
          <nav className="lg:hidden flex items-center gap-2 overflow-x-auto pb-3">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setSection(t.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  section === t.key
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {t.label}
                {t.key === 'messages' && (
                  <UnreadBadge
                    count={unread}
                    className="relative ml-1.5 align-middle"
                  />
                )}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <InstallAppBanner />

      <main className="max-w-6xl mx-auto px-6 py-8">
        {section === 'jour' && <TodayView />}
        {section === 'planning' && <PlanningView />}
        {section === 'eleves' && <StudentsView />}
        {section === 'equipe' && <TeamView />}
        {section === 'messages' && <MessagesView />}
      </main>

      <BilanModal />
      <LivretModal />
      <EvaluationModal />
      <LessonsModal />
    </div>
  );
}
